import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import type { Control, FieldErrors, UseFormSetValue } from 'react-hook-form'
import { Controller, useWatch } from 'react-hook-form'
import { catalogSelectMenuProps } from '@/features/rewards/lib/catalogSelectMenuProps'
import {
  CHILDREN_OPTIONS,
  EDUCATION_OPTIONS,
  EMPLOYMENT_OPTIONS,
  GENDER_OPTIONS,
  HOUSING_OPTIONS,
  HOUSEHOLD_OPTIONS,
  INDUSTRY_OPTIONS,
  MARITAL_OPTIONS,
  US_STATE_OPTIONS,
} from '@/features/rewards/lib/demographicCatalog'
import { getCitiesForState, resolveStateCode } from '@/features/rewards/lib/usLocationCatalog'
import { formatUsMobileOnBlur } from '@/features/account/lib/account-phone-utils'
import type { AccountSettingsFormData } from '@/features/account/lib/account-settings-schema'
import {
  accountFormEmailLockedSx,
  accountFormFieldCellSx,
  accountFormGridSx,
  accountFormHelperErrorSx,
  accountFormHelperInfoSx,
  accountFormInputSx,
  accountFormLabelSx,
  accountFormRequiredMarkSx,
  accountFormSelectSx,
} from '@/features/account/lib/account-settings-tokens'
import { EKYC_NAME_FIELDS_LOCKED_MESSAGE } from '@/features/ekyc/lib/ekycUi'

const EMAIL_LOCKED_TOOLTIP = 'Email cannot be changed'

const CATALOG_PLACEHOLDER = 'Select'
const NOT_PROVIDED_LABEL = 'Not Provided'

interface Props {
  control: Control<AccountSettingsFormData>
  errors: FieldErrors<AccountSettingsFormData>
  stateValue: string
  email: string
  setValue: UseFormSetValue<AccountSettingsFormData>
  /** When true, first/last name are read-only (eKYC under review or verified). */
  nameFieldsLocked?: boolean
}

function RequiredLabel({ children }: Readonly<{ children: string }>) {
  return (
    <Typography component="label" sx={accountFormLabelSx}>
      {children}
      <Box component="span" sx={accountFormRequiredMarkSx}>
        *
      </Box>
    </Typography>
  )
}

/** Overlays the 36px row gap — does not change grid row height (Figma 5729-11899) */
function AccountFieldHelper({
  message,
  variant = 'error',
}: Readonly<{ message?: string; variant?: 'error' | 'info' }>) {
  if (!message) {
    return null
  }

  const sx = variant === 'info' ? accountFormHelperInfoSx : accountFormHelperErrorSx

  return (
    <Typography
      component="p"
      sx={sx}
      aria-live={variant === 'error' ? 'polite' : undefined}
    >
      {message}
    </Typography>
  )
}

function fieldHelperMessage(
  error: string | undefined,
  value: string | undefined,
  disabled = false,
): { message: string; variant: 'error' | 'info' } | null {
  if (error) {
    return { message: error, variant: 'error' }
  }
  if (!value?.trim() && !disabled) {
    return { message: NOT_PROVIDED_LABEL, variant: 'info' }
  }
  return null
}

function AccountFieldHelperFromState(
  props: Readonly<{ error?: string; value?: string; disabled?: boolean }>,
) {
  const helper = fieldHelperMessage(props.error, props.value, props.disabled)
  if (!helper) return null
  return <AccountFieldHelper message={helper.message} variant={helper.variant} />
}

function CatalogSelect({
  value,
  onChange,
  options,
  error,
  testId,
  disabled = false,
  selectKey,
}: Readonly<{
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
  error?: string
  testId: string
  disabled?: boolean
  selectKey?: string
}>) {
  const selectedLabel = options.find((opt) => opt.value === value)?.label

  return (
    <>
      <FormControl fullWidth error={Boolean(error)}>
        <Select
          key={selectKey}
          value={value || ''}
          displayEmpty
          disabled={disabled}
          onChange={(e) => onChange(String(e.target.value))}
          MenuProps={catalogSelectMenuProps}
          renderValue={() =>
            selectedLabel ?? (
              <Box component="span" sx={{ color: '#94a3b8' }}>
                {CATALOG_PLACEHOLDER}
              </Box>
            )
          }
          sx={accountFormSelectSx}
          data-testid={testId}
        >
          <MenuItem value="" disabled>
            {CATALOG_PLACEHOLDER}
          </MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <AccountFieldHelperFromState error={error} value={value} disabled={disabled} />
    </>
  )
}

export function AccountDemographicForm({
  control,
  errors,
  stateValue,
  email,
  setValue,
  nameFieldsLocked = false,
}: Readonly<Props>) {
  const stateCode = resolveStateCode(stateValue)
  const cities = getCitiesForState(stateCode)
  const lockedFirstName = useWatch({ control, name: 'firstName' })
  const lockedLastName = useWatch({ control, name: 'lastName' })

  return (
    <Box sx={accountFormGridSx} data-testid="account-demographic-form">
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>First Name</RequiredLabel>
        {nameFieldsLocked ? (
          <>
            <Box
              sx={accountFormEmailLockedSx}
              data-testid="account-first-name"
              aria-readonly
              aria-label={EKYC_NAME_FIELDS_LOCKED_MESSAGE}
            >
              {lockedFirstName}
            </Box>
            <AccountFieldHelper message={EKYC_NAME_FIELDS_LOCKED_MESSAGE} variant="info" />
          </>
        ) : (
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  fullWidth
                  error={Boolean(errors.firstName)}
                  inputProps={{ 'data-testid': 'account-first-name' }}
                  sx={accountFormInputSx}
                />
                <AccountFieldHelper message={errors.firstName?.message} />
              </>
            )}
          />
        )}
      </Box>
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>Last Name</RequiredLabel>
        {nameFieldsLocked ? (
          <Box
            sx={accountFormEmailLockedSx}
            data-testid="account-last-name"
            aria-readonly
            aria-label={EKYC_NAME_FIELDS_LOCKED_MESSAGE}
          >
            {lockedLastName}
          </Box>
        ) : (
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  fullWidth
                  error={Boolean(errors.lastName)}
                  inputProps={{ 'data-testid': 'account-last-name' }}
                  sx={accountFormInputSx}
                />
                <AccountFieldHelper message={errors.lastName?.message} />
              </>
            )}
          />
        )}
      </Box>
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>Phone Number</RequiredLabel>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <>
              <TextField
                {...field}
                fullWidth
                error={Boolean(errors.phone)}
                placeholder="Enter phone number"
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={() => {
                  field.onBlur()
                  field.onChange(formatUsMobileOnBlur(field.value))
                }}
                inputProps={{ 'data-testid': 'account-phone', inputMode: 'tel' }}
                sx={accountFormInputSx}
              />
              <AccountFieldHelper message={errors.phone?.message} />
            </>
          )}
        />
      </Box>
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>Date of Birth</RequiredLabel>
        <Controller
          name="dateOfBirth"
          control={control}
          render={({ field }) => (
            <>
              <DatePicker
                value={field.value ? dayjs(field.value) : null}
                onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: 'Select date',
                    error: Boolean(errors.dateOfBirth),
                    inputProps: { 'data-testid': 'account-dob' },
                    sx: accountFormInputSx,
                  },
                }}
              />
              <AccountFieldHelper message={errors.dateOfBirth?.message} />
            </>
          )}
        />
      </Box>
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>Gender</RequiredLabel>
        <Controller
          name="gender"
          control={control}
          render={({ field }) => (
            <CatalogSelect
              value={field.value}
              onChange={field.onChange}
              options={GENDER_OPTIONS}
              error={errors.gender?.message}
              testId="account-gender"
            />
          )}
        />
      </Box>
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>Marital Status</RequiredLabel>
        <Controller
          name="maritalStatus"
          control={control}
          render={({ field }) => (
            <CatalogSelect
              value={field.value}
              onChange={field.onChange}
              options={MARITAL_OPTIONS}
              error={errors.maritalStatus?.message}
              testId="account-marital-status"
            />
          )}
        />
      </Box>
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>State</RequiredLabel>
        <Controller
          name="state"
          control={control}
          render={({ field }) => (
            <CatalogSelect
              value={resolveStateCode(field.value)}
              onChange={(nextState) => {
                if (nextState !== resolveStateCode(field.value)) {
                  setValue('city', '', { shouldValidate: true })
                }
                field.onChange(nextState)
              }}
              options={US_STATE_OPTIONS}
              error={errors.state?.message}
              testId="account-state"
            />
          )}
        />
      </Box>
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>City</RequiredLabel>
        <Controller
          name="city"
          control={control}
          render={({ field }) =>
            cities.length > 0 ? (
              <CatalogSelect
                selectKey={stateCode}
                value={field.value}
                onChange={field.onChange}
                options={cities}
                disabled={!stateCode}
                error={errors.city?.message}
                testId="account-city"
              />
            ) : (
              <>
                <TextField
                  {...field}
                  fullWidth
                  disabled={!stateCode}
                  error={Boolean(errors.city)}
                  placeholder={stateCode ? CATALOG_PLACEHOLDER : 'Select a state first'}
                  inputProps={{ 'data-testid': 'account-city' }}
                  sx={accountFormInputSx}
                />
                <AccountFieldHelperFromState
                  error={errors.city?.message}
                  value={field.value}
                  disabled={!stateCode}
                />
              </>
            )
          }
        />
      </Box>
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>Household Size</RequiredLabel>
        <Controller
          name="householdSize"
          control={control}
          render={({ field }) => (
            <CatalogSelect
              value={field.value}
              onChange={field.onChange}
              options={HOUSEHOLD_OPTIONS}
              error={errors.householdSize?.message}
              testId="account-household-size"
            />
          )}
        />
      </Box>
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>Email</RequiredLabel>
        <Tooltip title={EMAIL_LOCKED_TOOLTIP} arrow placement="top">
          <Box
            sx={accountFormEmailLockedSx}
            data-testid="account-email"
            aria-readonly
            aria-label={EMAIL_LOCKED_TOOLTIP}
          >
            {email}
          </Box>
        </Tooltip>
      </Box>
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>No of Children</RequiredLabel>
        <Controller
          name="numberOfChildren"
          control={control}
          render={({ field }) => (
            <CatalogSelect
              value={field.value}
              onChange={field.onChange}
              options={CHILDREN_OPTIONS}
              error={errors.numberOfChildren?.message}
              testId="account-children"
            />
          )}
        />
      </Box>
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>Industry/Field of Work</RequiredLabel>
        <Controller
          name="industry"
          control={control}
          render={({ field }) => (
            <CatalogSelect
              value={field.value}
              onChange={field.onChange}
              options={INDUSTRY_OPTIONS}
              error={errors.industry?.message}
              testId="account-industry"
            />
          )}
        />
      </Box>
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>Education Level</RequiredLabel>
        <Controller
          name="education"
          control={control}
          render={({ field }) => (
            <CatalogSelect
              value={field.value}
              onChange={field.onChange}
              options={EDUCATION_OPTIONS}
              error={errors.education?.message}
              testId="account-education"
            />
          )}
        />
      </Box>
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>Employment Status</RequiredLabel>
        <Controller
          name="employment"
          control={control}
          render={({ field }) => (
            <CatalogSelect
              value={field.value}
              onChange={field.onChange}
              options={EMPLOYMENT_OPTIONS}
              error={errors.employment?.message}
              testId="account-employment"
            />
          )}
        />
      </Box>
      <Box sx={accountFormFieldCellSx}>
        <RequiredLabel>Housing Status</RequiredLabel>
        <Controller
          name="housingStatus"
          control={control}
          render={({ field }) => (
            <CatalogSelect
              value={field.value}
              onChange={field.onChange}
              options={HOUSING_OPTIONS}
              error={errors.housingStatus?.message}
              testId="account-housing-status"
            />
          )}
        />
      </Box>
    </Box>
  )
}
